import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { marked } from 'marked';

import { createStyles, makeStyles } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import RemoveRoundedIcon from '@material-ui/icons/RemoveRounded';
import ArrowForwardIosRoundedIcon from '@material-ui/icons/ArrowForwardIosRounded';
import FormatListBulletedRoundedIcon from '@material-ui/icons/FormatListBulletedRounded';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

import IntlMessages, { useIntl } from 'util/IntlMessages';
import {
  SparkleIcon,
  CopyIcon,
  CheckMarkIcon,
  ThumbUpIcon,
  ThumbDownIcon,
  PaperPlaneIcon,
} from 'assets/icons';
import * as askManual from 'ducks/askManual';
import { AskStatus } from 'ducks/askManual';
import { ConfirmationModal } from 'new-design/components/ConfirmationModal';
import { TextInput, Button } from 'new-design/common/ui';

import { SECTIONS, sectionById } from './sections';

marked.setOptions({ gfm: true, breaks: true });

const useStyles = makeStyles((theme) => {
  const { primary, secondary, status, deepBlue, darkGrey, text } = theme.palette;

  const tint = deepBlue.bg; // rgba(2, 44, 230, 0.05) — brand primary tint
  const surface = secondary.light; // #F7F7F7 — light fill
  const surfaceHover = darkGrey.bg; // #F2F2F2 — hover fill
  const border = secondary.semiLight; // #E5E5E5 — neutral border
  const divider = darkGrey.bg; // #F2F2F2 — divider
  const white = primary.contrastText; // #fff
  const textMain = text.primary; // #222

  return createStyles({
    paper: {
      width: 420,
      height: 680,
      maxHeight: 'calc(100vh - 80px)',
      background: white,
      borderRadius: 14,
      boxShadow: '0 16px 48px rgba(34, 34, 34, 0.18)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '12px 14px 10px',
      borderBottom: `1px solid ${divider}`,
    },
    headerIcon: {
      width: 30,
      height: 30,
      borderRadius: 7,
      background: tint,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: primary.main,
      flexShrink: 0,
      '& svg': { width: 16, height: 16 },
      '& svg path[fill]:not([fill="none"])': { fill: 'currentColor' },
    },
    headerText: { flex: 1, minWidth: 0 },
    headerTitle: {
      fontWeight: 600,
      fontSize: 14,
      color: textMain,
      lineHeight: 1.2,
    },
    headerSubtitle: {
      fontSize: 11,
      color: secondary.main,
      marginTop: 1,
      lineHeight: 1.3,
    },
    headerActions: { display: 'flex', gap: 4, marginLeft: 'auto' },
    iconBtn: {
      width: 28,
      height: 28,
      padding: 0,
      color: secondary.main,
      '&:hover': { color: primary.main, background: tint },
    },
    thread: {
      flex: 1,
      minHeight: 80,
      overflowY: 'auto',
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-thumb': {
        background: border,
        borderRadius: 3,
      },
      '&::-webkit-scrollbar-thumb:hover': { background: secondary.main },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      scrollbarWidth: 'thin',
      scrollbarColor: `${border} transparent`,
    },
    typingBubble: {
      display: 'inline-flex',
      gap: 5,
      padding: '11px 14px',
      alignItems: 'center',
    },
    typingDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: primary.main,
      opacity: 0.4,
      animation: '$typingDot 1.2s ease-in-out infinite',
      '&:nth-child(2)': { animationDelay: '0.18s' },
      '&:nth-child(3)': { animationDelay: '0.36s' },
    },
    '@keyframes typingDot': {
      '0%, 60%, 100%': { opacity: 0.3, transform: 'scale(1)' },
      '30%': { opacity: 1, transform: 'scale(1.25)' },
    },
    userMsg: {
      alignSelf: 'flex-end',
      maxWidth: '85%',
      padding: '7px 11px',
      borderRadius: '12px 12px 2px 12px',
      background: primary.main,
      color: white,
      fontSize: 12.5,
      lineHeight: 1.4,
      wordBreak: 'break-word',
    },
    assistantGroup: {
      alignSelf: 'flex-start',
      maxWidth: '95%',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    copyBtn: {
      alignSelf: 'flex-start',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      border: 'none',
      background: 'transparent',
      color: secondary.main,
      fontSize: 11,
      fontWeight: 500,
      cursor: 'pointer',
      padding: '2px 6px',
      borderRadius: 6,
      transition: 'color 120ms ease, background 120ms ease',
      '&:hover': { color: primary.main, background: tint },
      '& svg': { width: 13, height: 13 },
      '& svg path[fill]:not([fill="none"])': { fill: 'currentColor' },
    },
    copyBtnCopied: {
      color: status.success,
      '&:hover': { color: status.success, background: tint },
    },
    actionsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    },
    feedbackGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      marginLeft: 'auto',
    },
    feedbackBtn: {
      width: 24,
      height: 24,
      padding: 0,
      color: secondary.main,
      '&:hover': { color: primary.main, background: tint },
      '& svg': { width: 13, height: 13 },
      // Same convention as the sibling icons (sparkle/copy): the SVG ships with
      // a #222222 fill; bind it to the button color so the thumb turns
      // grey / green / red / blue with state.
      '& svg path[fill]:not([fill="none"])': { fill: 'currentColor' },
    },
    feedbackBtnUp: {
      color: status.success,
      '&:hover': { color: status.success, background: tint },
    },
    feedbackBtnDown: {
      color: status.error,
      '&:hover': { color: status.error, background: tint },
    },
    assistantMsg: {
      alignSelf: 'flex-start',
      maxWidth: '100%',
      padding: '10px 13px',
      background: surface,
      border: `1px solid ${border}`,
      borderRadius: '12px 12px 12px 2px',
      fontSize: 12.5,
      lineHeight: 1.55,
      color: textMain,
      wordBreak: 'break-word',
      '& p': { margin: '0 0 8px' },
      '& p:last-child': { marginBottom: 0 },
      '& ol, & ul': { margin: '6px 0 8px 18px', padding: 0 },
      '& li': { marginBottom: 4 },
      '& strong': { fontWeight: 600 },
      '& code': {
        background: tint,
        padding: '1px 4px',
        borderRadius: 4,
        fontSize: 11.5,
      },
      '& h1, & h2, & h3': { fontSize: 13, fontWeight: 600, margin: '10px 0 4px' },
    },
    footer: {
      padding: '10px 14px 14px',
      borderTop: `1px solid ${divider}`,
      flexShrink: 0,
    },
    bodyScroll: {
      flex: 1,
      overflowY: 'auto',
      padding: '12px 14px',
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-thumb': {
        background: border,
        borderRadius: 3,
      },
      '&::-webkit-scrollbar-thumb:hover': { background: secondary.main },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      scrollbarWidth: 'thin',
      scrollbarColor: `${border} transparent`,
    },
    topicBar: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 14px',
      borderBottom: `1px solid ${divider}`,
      background: tint,
      flexShrink: 0,
    },
    topicToggle: {
      width: 28,
      height: 28,
      padding: 0,
      color: secondary.main,
      border: `1px solid ${border}`,
      borderRadius: 7,
      '&:hover': { color: primary.main, background: tint },
    },
    topicToggleOn: {
      color: primary.main,
      borderColor: primary.light,
      background: tint,
    },
    topicLabel: {
      flex: 1,
      fontSize: 12.5,
      color: secondary.main,
      '& strong': { color: primary.main, fontWeight: 600 },
    },
    changeTopicBtn: {
      color: primary.main,
      padding: '4px 6px',
      borderRadius: 6,
      whiteSpace: 'nowrap',
      '& .MuiButton-label': { fontSize: 12.5, fontWeight: 600 },
      '&:hover': { background: tint },
    },
    stepRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
    },
    stepBadge: {
      width: 19,
      height: 19,
      borderRadius: '50%',
      background: primary.main,
      color: white,
      fontWeight: 600,
      fontSize: 11,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    stepLabel: { fontSize: 13, fontWeight: 600, color: textMain },
    sections: { display: 'flex', flexDirection: 'column', gap: 2 },
    sectionItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '5px 8px',
      borderRadius: 8,
      cursor: 'pointer',
      border: '1px solid transparent',
      transition: 'background 120ms ease, border-color 120ms ease',
      '&:hover': { background: tint },
    },
    sectionItemSelected: {
      background: tint,
      borderColor: primary.light,
    },
    radio: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      border: `2px solid ${secondary.main}`,
      flexShrink: 0,
      position: 'relative',
    },
    radioSelected: {
      borderColor: primary.main,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 2,
        left: 2,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: primary.main,
      },
    },
    sectionIcon: {
      width: 26,
      height: 26,
      borderRadius: 7,
      background: tint,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: primary.main,
      flexShrink: 0,
      '& svg': { width: 14, height: 14 },
      // Normalize mixed icon sources (MUI inherit currentColor vs. custom SVGs
      // painted with the theme grey) so every topic icon shares the same color.
      '& svg path[fill]:not([fill="none"])': { fill: 'currentColor' },
      '& svg path[stroke]:not([stroke="none"])': { stroke: 'currentColor' },
    },
    sectionLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: 12.5,
      fontWeight: 600,
      color: textMain,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    popularBlock: { marginTop: 14 },
    popularHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: 0.7,
      color: primary.main,
      textTransform: 'uppercase',
      marginBottom: 6,
    },
    popularDot: {
      width: 4,
      height: 4,
      borderRadius: '50%',
      background: primary.main,
    },
    popularList: { display: 'flex', flexDirection: 'column', gap: 4 },
    popularItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      padding: '7px 10px',
      background: surface,
      borderRadius: 8,
      fontSize: 12,
      color: textMain,
      cursor: 'pointer',
      border: '1px solid transparent',
      transition: 'background 120ms ease, border-color 120ms ease',
      '&:hover': {
        background: surfaceHover,
        borderColor: primary.light,
      },
      '& svg': { width: 12, height: 12, color: secondary.main, flexShrink: 0 },
    },
    questionStep: { marginTop: 14 },
    composer: {
      marginTop: 8,
      display: 'flex',
      alignItems: 'flex-end',
      gap: 8,
    },
    field: {
      flex: 1,
      '& .MuiOutlinedInput-root': { borderRadius: 24 },
      '& .MuiOutlinedInput-multiline': { padding: '7px 12px' },
      '& .MuiInputBase-inputMultiline': {
        padding: 0,
        fontSize: 13,
        lineHeight: 1.4,
        color: textMain,
      },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: primary.main },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: primary.main,
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: primary.main,
        borderWidth: 1,
      },
      '& textarea': {
        overflowY: 'auto !important',
        scrollbarWidth: 'thin',
        scrollbarColor: `${border} transparent`,
        '&::placeholder': { color: secondary.main, opacity: 1 },
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { background: border, borderRadius: 3 },
      },
    },
    sendBtn: {
      width: 36,
      height: 36,
      padding: 0,
      borderRadius: '50%',
      background: primary.main,
      color: white,
      flexShrink: 0,
      '&:hover': { background: primary.main, opacity: 0.9 },
      '&.Mui-disabled': {
        background: secondary.semiLight,
        color: secondary.main,
      },
      '& svg': { width: 18, height: 18 },
      '& svg path[fill]:not([fill="none"])': { fill: 'currentColor' },
    },
    errorBox: {
      padding: '10px 12px',
      background: theme.palette.red.bg,
      border: `1px solid ${status.error}`,
      borderRadius: 10,
      fontSize: 12.5,
      color: status.error,
    },
  });
});

const renderMarkdown = (text) => {
  if (!text) return '';
  try {
    return marked.parse(text);
  } catch (_) {
    return text;
  }
};

const Panel = ({ onClose, onMinimize }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const category = useSelector(askManual.selectCategory);
  const status = useSelector(askManual.selectStatus);
  const messages = useSelector(askManual.selectMessages);
  const error = useSelector(askManual.selectError);
  const feedback = useSelector(askManual.selectFeedback);

  const [draft, setDraft] = useState('');
  const [pickingTopic, setPickingTopic] = useState(false);
  const [showPopular, setShowPopular] = useState(false);
  const [confirmingChange, setConfirmingChange] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const threadRef = useRef(null);
  const threadEndRef = useRef(null);
  const inputRef = useRef(null);
  const copyTimerRef = useRef(null);

  const activeSection = useMemo(() => sectionById(category), [category]);
  const hasChat = messages.length > 0;
  const showPicker = !hasChat || pickingTopic;
  const loading = status === AskStatus.LOADING;

  // Smart auto-scroll: only stick to bottom if user is already near the bottom.
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom && threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, loading]);

  // Re-focus input when chat becomes idle again (after sending) and when view changes.
  useEffect(() => {
    if (!loading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading, showPicker]);

  // Clear the pending "copied" reset timer on unmount.
  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const handleCopy = useCallback((text, index) => {
    const done = () => {
      setCopiedIndex(index);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedIndex(null), 1800);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    }
  }, []);

  const handleFeedback = useCallback(
    (logID, helpful) => {
      if (logID == null) return;
      dispatch(askManual.actions.feedback({ logID, helpful }));
    },
    [dispatch]
  );

  const handleSelectCategory = useCallback(
    (id) => {
      dispatch(askManual.actions.setCategory(id));
      if (hasChat) setPickingTopic(false);
    },
    [dispatch, hasChat]
  );

  const submit = useCallback(
    (question) => {
      const trimmed = (question || '').trim();
      if (!trimmed) return;
      dispatch(askManual.actions.ask({ category, question: trimmed }));
      setPickingTopic(false);
      setShowPopular(false);
    },
    [dispatch, category]
  );

  const handleSend = useCallback(() => {
    submit(draft);
    setDraft('');
  }, [submit, draft]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handlePopularClick = useCallback(
    (key) => {
      submit(formatMessage({ id: key }));
    },
    [formatMessage, submit]
  );

  const handleConfirmChange = useCallback(() => {
    dispatch(askManual.actions.reset());
    setConfirmingChange(false);
    setPickingTopic(false);
    setShowPopular(false);
    setDraft('');
  }, [dispatch]);

  const handleChangeTopicClick = useCallback(() => {
    if (messages.length > 0) {
      setConfirmingChange(true);
    } else {
      setPickingTopic(true);
    }
  }, [messages.length]);

  const sendDisabled = !draft.trim() || loading;

  const renderInput = (compact = false) => (
    <div className={compact ? undefined : classes.questionStep}>
      {!compact && (
        <div className={classes.stepRow}>
          <div className={classes.stepBadge}>2</div>
          <div className={classes.stepLabel}>
            <IntlMessages id="your_question" />
          </div>
        </div>
      )}
      <div className={classes.composer}>
        <TextInput
          inputRef={inputRef}
          className={classes.field}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage({ id: 'ask_manual_placeholder' })}
          multiline
          rows={1}
          rowsMax={4}
          fullWidth
          autoFocus
          inputProps={{ maxLength: 1000 }}
        />
        <IconButton
          size="small"
          className={classes.sendBtn}
          onClick={handleSend}
          disabled={sendDisabled}
          aria-label={formatMessage({ id: 'send_question' })}
        >
          <PaperPlaneIcon />
        </IconButton>
      </div>
    </div>
  );

  const renderPopular = () => (
    <div className={classes.popularList}>
      {activeSection.popularKeys.map((key) => (
        <div key={key} className={classes.popularItem} onClick={() => handlePopularClick(key)}>
          <span>
            <IntlMessages id={key} />
          </span>
          <ArrowForwardIosRoundedIcon />
        </div>
      ))}
    </div>
  );

  const renderTopicBar = () => (
    <div className={classes.topicBar}>
      <Tooltip title={formatMessage({ id: 'popular_questions' })} placement="bottom" arrow>
        <IconButton
          size="small"
          className={clsx(classes.topicToggle, { [classes.topicToggleOn]: showPopular })}
          onClick={() => setShowPopular((prev) => !prev)}
        >
          <FormatListBulletedRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <span className={classes.topicLabel}>
        <IntlMessages id="topic" />{' '}
        <strong>
          <IntlMessages id={activeSection.labelKey} />
        </strong>
      </span>
      <Button variant="text" className={classes.changeTopicBtn} onClick={handleChangeTopicClick}>
        <IntlMessages id="change_topic" />
      </Button>
    </div>
  );

  return (
    <div className={classes.paper}>
      <div className={classes.header}>
        <div className={classes.headerIcon}>
          <SparkleIcon />
        </div>
        <div className={classes.headerText}>
          <div className={classes.headerTitle}>
            <IntlMessages id="ask_manual_title" />
          </div>
          <div className={classes.headerSubtitle}>
            <IntlMessages id="ask_manual_subtitle" />
          </div>
        </div>
        <div className={classes.headerActions}>
          <IconButton size="small" className={classes.iconBtn} onClick={onMinimize}>
            <RemoveRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" className={classes.iconBtn} onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Chat mode: sticky topic bar + thread + footer with popular + input */}
      {hasChat && !showPicker && (
        <>
          {renderTopicBar()}

          <div className={classes.thread} ref={threadRef}>
            {messages.map((m, i) =>
              m.role === 'user' ? (
                <div key={i} className={classes.userMsg}>
                  {m.text}
                </div>
              ) : (
                <div key={i} className={classes.assistantGroup}>
                  <div
                    className={classes.assistantMsg}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }}
                  />
                  <div className={classes.actionsRow}>
                    <button
                      type="button"
                      className={clsx(classes.copyBtn, {
                        [classes.copyBtnCopied]: copiedIndex === i,
                      })}
                      onClick={() => handleCopy(m.text, i)}
                    >
                      {copiedIndex === i ? <CheckMarkIcon /> : <CopyIcon />}
                      <IntlMessages
                        id={copiedIndex === i ? 'copied' : 'copy'}
                      />
                    </button>
                    {m.logID != null && (
                      <div className={classes.feedbackGroup}>
                        <Tooltip
                          title={formatMessage({ id: 'helpful' })}
                          placement="top"
                          arrow
                        >
                          <IconButton
                            size="small"
                            className={clsx(classes.feedbackBtn, {
                              [classes.feedbackBtnUp]: feedback[m.logID] === true,
                            })}
                            onClick={() => handleFeedback(m.logID, true)}
                          >
                            <ThumbUpIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={formatMessage({ id: 'not_helpful' })}
                          placement="top"
                          arrow
                        >
                          <IconButton
                            size="small"
                            className={clsx(classes.feedbackBtn, {
                              [classes.feedbackBtnDown]: feedback[m.logID] === false,
                            })}
                            onClick={() => handleFeedback(m.logID, false)}
                          >
                            <ThumbDownIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
            {loading && (
              <div className={clsx(classes.assistantMsg, classes.typingBubble)}>
                <span className={classes.typingDot} />
                <span className={classes.typingDot} />
                <span className={classes.typingDot} />
              </div>
            )}
            {error && (
              <div className={classes.errorBox}>
                <IntlMessages id={error} />
              </div>
            )}
            <div ref={threadEndRef} />
          </div>

          <div className={classes.footer}>
            {showPopular && (
              <div className={classes.popularBlock}>
                <div className={classes.popularHeader}>
                  <span className={classes.popularDot} />
                  <IntlMessages id="ask_manual_popular_prefix" />{' '}
                  <IntlMessages id={activeSection.labelKey} />
                </div>
                {renderPopular()}
              </div>
            )}
            {renderInput(true)}
          </div>
        </>
      )}

      {/* Picker mode */}
      {showPicker && (
        <div className={classes.bodyScroll}>
          <div className={classes.stepRow}>
            <div className={classes.stepBadge}>1</div>
            <div className={classes.stepLabel}>
              <IntlMessages id="whats_your_question_about" />
            </div>
          </div>

          <div className={classes.sections}>
            {SECTIONS.map(({ id, Icon, labelKey, descKey }) => {
              const selected = id === category;
              return (
                <Tooltip
                  key={id}
                  title={formatMessage({ id: descKey })}
                  placement="right"
                  enterDelay={150}
                  arrow
                >
                  <div
                    className={clsx(classes.sectionItem, {
                      [classes.sectionItemSelected]: selected,
                    })}
                    onClick={() => handleSelectCategory(id)}
                    role="radio"
                    aria-checked={selected}
                  >
                    <div className={clsx(classes.radio, { [classes.radioSelected]: selected })} />
                    <div className={classes.sectionIcon}>
                      <Icon />
                    </div>
                    <span className={classes.sectionLabel}>
                      <IntlMessages id={labelKey} />
                    </span>
                  </div>
                </Tooltip>
              );
            })}
          </div>

          <div className={classes.popularBlock}>
            <div className={classes.popularHeader}>
              <span className={classes.popularDot} />
              <IntlMessages id="ask_manual_popular_prefix" />{' '}
              <IntlMessages id={activeSection.labelKey} />
            </div>
            {renderPopular()}
          </div>

          {renderInput(false)}
        </div>
      )}

      <ConfirmationModal
        modalOpened={confirmingChange}
        title={formatMessage({ id: 'change_topic_confirm_title' })}
        description={formatMessage({ id: 'change_topic_confirm_text' })}
        customConfirmMessage={formatMessage({ id: 'change_topic' })}
        onModalCancel={() => setConfirmingChange(false)}
        onModalConfirm={handleConfirmChange}
      />
    </div>
  );
};

export default Panel;
